import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Eye, Globe, Clock, Activity } from 'lucide-react';
import { format } from 'date-fns';

interface AccessLog {
  id: string;
  email: string;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  ip_address: string | null;
  geo: any;
  data_room_files: {
    file_name: string;
  } | null;
}

interface DataRoomAccessAnalyticsProps {
  companyId: string;
}

export default function DataRoomAccessAnalytics({ companyId }: DataRoomAccessAnalyticsProps) {
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAccess: 0,
    uniqueVisitors: 0,
    avgDuration: 0,
    activeNow: 0,
  });

  async function loadLogs() {
    setLoading(true);
    const { data, error } = await supabase
      .from('data_room_access_log')
      .select('*, data_room_files(file_name)')
      .eq('company_id', companyId)
      .order('started_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error loading access logs:', error);
    } else if (data) {
      setLogs(data);

      // Calculate statistics
      const uniqueEmails = new Set(data.map((log) => log.email));
      const durations = data
        .filter((log) => log.duration_ms !== null)
        .map((log) => log.duration_ms as number);
      const avgDuration =
        durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

      // Active in last 5 minutes
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      const activeNow = data.filter(
        (log) => log.ended_at === null || new Date(log.ended_at) > fiveMinutesAgo
      ).length;

      setStats({
        totalAccess: data.length,
        uniqueVisitors: uniqueEmails.size,
        avgDuration: avgDuration,
        activeNow,
      });
    }
    setLoading(false);
  }

  useEffect(() => {
    loadLogs();

    // Refresh every 30 seconds
    const interval = setInterval(loadLogs, 30000);
    return () => clearInterval(interval);
  }, [companyId]);

  function formatDuration(ms: number | null) {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  function formatLocation(geo: any) {
    if (!geo) return 'Unknown';
    const parts = [];
    if (geo.city) parts.push(geo.city);
    if (geo.country) parts.push(geo.country);
    return parts.join(', ') || 'Unknown';
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Eye className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats.totalAccess}</p>
                <p className="text-xs text-muted-foreground">Total Access</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats.uniqueVisitors}</p>
                <p className="text-xs text-muted-foreground">Unique Visitors</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{formatDuration(stats.avgDuration)}</p>
                <p className="text-xs text-muted-foreground">Avg. Duration</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{stats.activeNow}</p>
                <p className="text-xs text-muted-foreground">Active Now</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Access Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-muted-foreground" />
            </div>
          ) : logs.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const isActive =
                      !log.ended_at ||
                      new Date(log.ended_at) > new Date(Date.now() - 5 * 60 * 1000);

                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {log.data_room_files?.file_name || 'Unknown'}
                        </TableCell>
                        <TableCell>{log.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Globe className="h-3 w-3 text-muted-foreground" />
                            {formatLocation(log.geo)}
                          </div>
                        </TableCell>
                        <TableCell>{format(new Date(log.started_at), 'MMM d, HH:mm')}</TableCell>
                        <TableCell>{formatDuration(log.duration_ms)}</TableCell>
                        <TableCell>
                          {isActive ? (
                            <Badge variant="default" className="bg-green-600">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Completed</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No access activity yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
